import { useCallback, useEffect, useMemo, useState } from "react";

import { api } from "../../api/client";
import type { CalendarResponse, LeaveStatus, Site } from "../../types";
import { formatLeaveStatus } from "../../utils/formatters";

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export default function AbsenceCalendar() {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [siteId, setSiteId] = useState<string>("");
  const [sites, setSites] = useState<Site[]>([]);
  const [data, setData] = useState<CalendarResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const month = monthKey(cursor);

  useEffect(() => {
    void (async () => {
      try {
        setSites(await api.get<Site[]>("/api/sites"));
      } catch {
        setSites([]);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams({ month });
      if (siteId) params.set("site_id", siteId);
      setData(
        await api.get<CalendarResponse>(
          `/api/leave/calendar?${params.toString()}`,
        ),
      );
    } catch (err) {
      setError((err as Error).message);
    }
  }, [month, siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const year = cursor.getFullYear();
  const monthIdx = cursor.getMonth();
  const total = daysInMonth(year, monthIdx);
  const days = Array.from({ length: total }, (_, i) => i + 1);

  const holidayDates = useMemo(() => {
    return new Set((data?.holidays ?? []).map((h) => h.date));
  }, [data?.holidays]);

  const entriesByEmployee = useMemo(() => {
    const map = new Map<number, CalendarResponse["entries"]>();
    for (const e of data?.entries ?? []) {
      const arr = map.get(e.employee_id) ?? [];
      arr.push(e);
      map.set(e.employee_id, arr);
    }
    return map;
  }, [data?.entries]);

  const go = (delta: number) => {
    const next = new Date(cursor);
    next.setMonth(next.getMonth() + delta);
    setCursor(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Absence Calendar</h1>
          <p className="text-sm text-gray-500">
            Approved leave wall-planner.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(-1)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
          >
            ←
          </button>
          <span className="min-w-[8rem] text-center text-sm font-semibold">
            {cursor.toLocaleDateString(undefined, {
              month: "long",
              year: "numeric",
            })}
          </span>
          <button
            type="button"
            onClick={() => go(1)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
          >
            →
          </button>
          <select
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">All sites</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-500">
                Employee
              </th>
              {days.map((d) => {
                const iso = `${year}-${String(monthIdx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                const dow = new Date(iso + "T00:00:00").getDay();
                const isWeekend = dow === 0 || dow === 6;
                const isHoliday = holidayDates.has(iso);
                return (
                  <th
                    key={d}
                    className={[
                      "px-1 py-2 text-center font-normal",
                      isWeekend ? "bg-gray-100 text-gray-400" : "",
                      isHoliday ? "bg-gray-200 text-gray-600" : "",
                    ].join(" ")}
                    title={isHoliday ? "Public holiday" : ""}
                  >
                    {d}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.employees ?? []).map((emp) => {
              const entries = entriesByEmployee.get(emp.id) ?? [];
              return (
                <tr key={emp.id}>
                  <td className="sticky left-0 bg-white px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                    {emp.name}
                  </td>
                  {days.map((d) => {
                    const iso = `${year}-${String(monthIdx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                    const match = entries.find(
                      (e) => iso >= e.start_date && iso <= e.end_date,
                    );
                    const dow = new Date(iso + "T00:00:00").getDay();
                    const isWeekend = dow === 0 || dow === 6;
                    const isHoliday = holidayDates.has(iso);
                    const bg = match
                      ? match.colour
                      : isHoliday
                        ? "#d1d5db"
                        : isWeekend
                          ? "#f3f4f6"
                          : "";
                    const opacity = match?.status === "pending" ? 0.5 : 1;
                    return (
                      <td
                        key={d}
                        title={
                          match
                            ? `${match.leave_type_name} — ${formatLeaveStatus(match.status as LeaveStatus)}`
                            : isHoliday
                              ? "Public holiday"
                              : ""
                        }
                        style={match ? { backgroundColor: bg, opacity } : undefined}
                        className={[
                          "h-8 w-6 border-l border-gray-100 px-0 py-0 text-center",
                          !match && isHoliday ? "bg-gray-300" : "",
                          !match && isWeekend ? "bg-gray-100" : "",
                        ].join(" ")}
                      />
                    );
                  })}
                </tr>
              );
            })}
            {(data?.employees ?? []).length === 0 && (
              <tr>
                <td
                  colSpan={total + 1}
                  className="px-3 py-6 text-center text-gray-400"
                >
                  No employees match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
